<template>
  <div class="government-bond-products" data-testid="government-bond-products">
    <section v-if="filters" class="government-bond-products__header">
      <div class="government-bond-products__header-container">
        <FilterList
          v-if="filters"
          v-model="selectedFilter"
          :options="filters.options"
          @change="applyFilter"
        />
      </div>
    </section>
    <LoaderIndicator
      v-if="isLoading"
      class="government-bond-products__loader"
      size="32"
      with-container
    />
    <HelperViewStatus
      v-else-if="hasError"
      class="government-bond-products__helper"
      title="Não foi possível carregar as informações"
      description="Aguarde alguns minutos e tente novamente."
      button-label="Tentar novamente"
      status="alert"
      @click="retry"
    />
    <section v-else class="government-bond-products__table">
      <MarketStatus v-if="marketStatus" :status="marketStatus" />
      <DataTable
        :columns="columnsTable"
        :data="products"
        show-divider
        has-interaction
        :status="isSorting ? 'loading' : 'default'"
        @on-sort="onSort"
        @on-row-click="product => handleProductClick(product)"
      />
      <LoaderIndicator
        v-if="isScrolling"
        class="government-bond-products__table-loader"
        size="32"
        with-container
      />
    </section>
    <ButtonIconPrimary
      v-if="hasScrolled"
      :class="scrolledButtonClass"
      label="Ir para o inicio"
      icon="EA0010"
      size="large"
      @click="goToTop"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, onBeforeMount, onMounted, onUnmounted, ref } from 'vue';

import {
  FilterList,
  LoaderIndicator,
  HelperViewStatus,
  DataTable,
  ButtonIconPrimary
} from '@warrenbrasil/nebraska-web';
import { MarketStatus } from '@/modules/catalog/components';

import { registerObserver } from '@/modules/catalog/views/Products/helpers/registerObserver';
import { columns } from './columns';
import {
  getGovernmentBondProducts,
  IGovernmentBondProduct,
  IGovernmentBondFilter,
  IPagination,
  IGovernmentBondParams,
  IMarketStatus
} from '@/modules/catalog/services/products';

interface Props {
  isBuilder: boolean;
}
const props = defineProps<Props>();

interface EmitEvents {
  (event: 'select-product', id: string): string;
}
const emit = defineEmits<EmitEvents>();

const products = ref<IGovernmentBondProduct[]>([]);
const filters = ref<IGovernmentBondFilter>();
const pagination = ref<IPagination>();
const marketStatus = ref<IMarketStatus>();
const params = ref<IGovernmentBondParams>({
  pageIndex: 1,
  pageSize: 10,
  segmentIds: '0'
});

const selectedFilter = ref('0');

const isLoading = ref(true);
const isScrolling = ref(false);
const isSorting = ref(false);
const hasError = ref(false);
const scrollTop = ref(document.documentElement.scrollTop);

const columnsTable = computed(() => {
  return columns(
    pagination.value!.totalRecords,
    marketStatus.value!.isMarketOpen
  );
});

const hasScrolled = computed(() => {
  return scrollTop.value >= 100;
});

const scrolledButtonClass = computed(() => {
  return props.isBuilder
    ? 'government-bond-products__button builder'
    : 'government-bond-products__button';
});

async function applyFilter(applyedFilter: string | number) {
  params.value = {
    pageSize: 10,
    pageIndex: 1,
    segmentIds: String(applyedFilter)
  };

  await fetchGovernmentBondProducts();
  registerObserver(fetchGovernmentBondProducts);
}

async function onSort(index: number, isSort: boolean, value: string) {
  const sortDirection =
    index === -1 ? undefined : isSort ? 'Ascending' : 'Descending';
  const sortField = index === -1 ? undefined : value;

  params.value = {
    ...params.value,
    pageIndex: 1,
    sortField,
    sortDirection
  };

  await fetchGovernmentBondProducts(false, false, true);
  registerObserver(fetchGovernmentBondProducts);
}

async function retry() {
  await fetchGovernmentBondProducts();
  registerObserver(fetchGovernmentBondProducts);
}

function goToTop() {
  document.documentElement.scrollTo(0, 0);
}

async function fetchGovernmentBondProducts(
  loading = true,
  scrolling = false,
  sorting = false
) {
  try {
    isLoading.value = loading;
    isScrolling.value = scrolling;
    isSorting.value = sorting;
    hasError.value = false;

    if (isScrolling.value) {
      if (!pagination.value?.nextPage) return;

      params.value = {
        ...params.value,
        pageIndex: pagination.value.nextPage
      };
    }

    const data = await getGovernmentBondProducts(params.value);

    filters.value = data.filters;
    pagination.value = data.pagination;
    marketStatus.value = data.marketStatus;

    const items = data.items.map(item => {
      return {
        ...item
      };
    });

    if (isScrolling.value) {
      products.value.push(...items);
      return;
    }

    products.value = items;
  } catch {
    hasError.value = true;
  } finally {
    isLoading.value = false;
    isScrolling.value = false;
    isSorting.value = false;
  }
}

function handleScrollTop() {
  scrollTop.value = document.documentElement.scrollTop;
}

function handleProductClick(product: IGovernmentBondProduct) {
  emit('select-product', product.productId);

  const routerName = props.isBuilder
    ? `catalog/builder/government-bond/${product.productId}`
    : `products-view/catalog/government-bond/${product.productId}`;

  window.history.pushState({}, '', `/app/#/${routerName}`);
}

onMounted(async () => {
  await fetchGovernmentBondProducts();

  registerObserver(fetchGovernmentBondProducts);
});

onBeforeMount(() => {
  window.addEventListener('scroll', handleScrollTop);
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScrollTop);
});
</script>

<style lang="less" scoped>
.government-bond-products {
  :deep(.table__header .table-row) {
    height: 72px;
  }

  :deep(.table__header .table-row .header-cell) {
    .text-p-5();
    font-weight: 600;
    text-align: right;
    padding: @size-spacing-x350 @size-spacing-x400;
  }

  :deep(.header-cell__icon) {
    margin-left: @size-spacing-x300;
  }

  :deep(.table-row .cell p) {
    &:first-of-type {
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
  }

  &__header {
    background-color: @background-secondary;
    position: sticky;
    top: 64px;
    left: 0;
    z-index: 20;
    width: 100%;
    height: 64px;
    border-top: @size-border-x400 solid @divider-primary;
    border-bottom: @size-border-x400 solid @divider-primary;
    display: flex;
    justify-content: flex-start;
    align-items: center;

    @media only screen and (max-width: 640px) {
      top: 60px;
    }
  }

  &__header-container {
    padding: 0 @size-spacing-x1100;
    max-width: 1152px;
    width: 1152px;
    margin: 0 auto;
  }

  &__helper {
    margin: @size-spacing-x800 auto 0;
    justify-content: center;
  }

  &__table {
    padding: 0 @size-spacing-x1100;
    max-width: 1152px;
    margin: @size-spacing-x800 auto;

    :deep(.table__body .table-row) {
      height: 90px;
    }

    &-loader {
      margin: @size-spacing-x800 auto @size-spacing-x800;
      min-height: 0 !important;
    }
  }

  &__button {
    position: fixed;
    bottom: 32px;
    right: 72px;

    &.builder {
      bottom: 112px;
    }

    @media only screen and (min-width: 1150px) {
      right: calc((100vw - 1008px) / 2);
    }
  }
}
</style>
